import { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'antd';
import { useMutation } from '@tanstack/react-query';
import type { ShiptimeShipmentResponse } from '@shipium-corp/shipium-ts-types';
import { runRuleTest } from '~/services/shiptime-service';
import { useSession } from '~/hooks/useSession';
import { buildRuleTestRequest } from './buildRuleTestRequest';
import type { RuleTestFormValues, RuleTestShipFromAddress } from './buildRuleTestRequest';
import { RuleTestForm } from './RuleTestForm';
import { RuleTestResults } from './RuleTestResults';
import { useRuleTestData } from './useRuleTestData';

export interface RuleTestModalProps {
  open: boolean;
  onClose: () => void;
}

interface CallRuleTestArgs {
  values: RuleTestFormValues;
  shipFromAddress: RuleTestShipFromAddress;
}

export const RuleTestModal = ({ open, onClose }: RuleTestModalProps) => {
  const [form] = Form.useForm<RuleTestFormValues>();
  const { sessionPartnerId } = useSession();
  const [response, setResponse] = useState<ShiptimeShipmentResponse | undefined>(undefined);
  const [submittedValues, setSubmittedValues] = useState<RuleTestFormValues | undefined>(undefined);

  const selectedFcId = Form.useWatch('fulfillmentContextId', form);
  const watchedOptedInInactiveRuleIds = Form.useWatch('inactiveRuleIdsToApply', form);
  const optedInInactiveRuleIds = submittedValues?.inactiveRuleIdsToApply ?? watchedOptedInInactiveRuleIds;

  const {
    fcOptions,
    fcsLoading,
    disabledRules,
    methodNameById,
    carrierNameById,
    activeRulesInOrder,
    carrierTreeData,
    shipFromAddress,
  } = useRuleTestData({
    open,
    selectedFcId,
    optedInInactiveRuleIds,
  });

  const runTest = useMutation({
    mutationFn: ({ values, shipFromAddress }: CallRuleTestArgs) =>
      runRuleTest(buildRuleTestRequest(values, shipFromAddress), sessionPartnerId),
    onSuccess: (data, variables) => {
      setResponse(data);
      setSubmittedValues(variables.values);
    },
  });

  // Reset transient state when the modal is reopened.
  useEffect(() => {
    if (open) {
      setResponse(undefined);
      setSubmittedValues(undefined);
      runTest.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFinish = (values: RuleTestFormValues) => {
    if (!shipFromAddress) return;
    runTest.mutate({ values, shipFromAddress });
  };

  const selectedMethodId = response?.carrierSelection?.carrierServiceMethodId;
  const selectedMethodName =
    (selectedMethodId ? methodNameById[selectedMethodId] : undefined) ??
    response?.carrierSelection?.serviceMethodName ??
    selectedMethodId ??
    '(unknown)';
  const selectedCarrier = response?.carrierSelection?.carrier;
  const selectedCarrierName = selectedCarrier ? (carrierNameById[selectedCarrier] ?? selectedCarrier) : undefined;

  return (
    <Modal
      title="Test Rule Configuration"
      width={720}
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={
        response ? null : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              loading={runTest.isPending}
              disabled={!selectedFcId || !shipFromAddress}
              title={
                !selectedFcId
                  ? 'Pick a fulfillment context first'
                  : !shipFromAddress
                    ? "This FC's origin has no country / postal code configured"
                    : undefined
              }
              onClick={() => form.submit()}
            >
              Run test
            </Button>
          </>
        )
      }
    >
      {response ? (
        <RuleTestResults
          response={response}
          onRunAgain={() => {
            setResponse(undefined);
            setSubmittedValues(undefined);
          }}
          selectedMethodName={selectedMethodName}
          selectedCarrierName={selectedCarrierName}
          methodNameById={methodNameById}
          activeRulesInOrder={activeRulesInOrder}
        />
      ) : (
        <RuleTestForm
          form={form}
          onFinish={handleFinish}
          fcOptions={fcOptions}
          fcsLoading={fcsLoading}
          disabledRules={disabledRules}
          carrierTreeData={carrierTreeData}
          errorMessage={runTest.isError ? ((runTest.error as Error)?.message ?? 'Unknown error') : undefined}
        />
      )}
    </Modal>
  );
};
